import { Property } from '@codefluegel/wowis-rest-typescript-client';
import {
  Box,
  BoxProps,
  CardActionArea,
  Tab,
  Tabs,
  Typography,
  styled,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useState } from 'react';

import moment from 'moment';
import useMedia from '../../hooks/useMedia';
import useReports from '../../hooks/useReports';
import ReportsInformationDialog from '../dialog/ReportsInformationDialog';

const Container = styled(Box)<BoxProps>(({ theme }) => ({
  height: '100%',
  minHeight: 500,
  backgroundColor: theme.palette.secondary.light,
}));

const ReportBoxContainer = styled(Box)<BoxProps>(({ theme }) => ({
  minHeight: '500px',
  bgcolor: 'white',
  backgroundColor: theme.palette.background.paper,
}));

const ReportBox = styled(Box)<BoxProps>(({ theme }) => ({
  maxHeight: '200px',
  backgroundColor: theme.palette.secondary.light,
  padding: 20,
}));

interface TabPanelProps {
  index: number;
  status: string[];
}

interface Props {
  currentEstate: Property | undefined;
}

const ReportsTabs: FC<Props> = observer(props => {
  const { currentEstate } = props;
  const { allReports, activeCategoryIndex, setActiveCategoryIndex } =
    useReports(currentEstate);
  const isMobile = useMedia('md');

  const handleChange = (event: React.SyntheticEvent, categoryIndex: number) => {
    setActiveCategoryIndex(categoryIndex);
  };

  const categories = [
    { id: 'todo', label: 'Eingereicht', status: ['Eingereicht'] },
    {
      id: 'inProgress',
      label: 'In Arbeit',
      status: ['Offen', 'Offen bei VU', 'Anwalt', 'Auftrag angelegt'],
    },
    {
      id: 'rejected',
      label: 'Abgelehnt',
      status: ['Abgelehnt', 'Mängelrüge angelegt'],
    },
    { id: 'done', label: 'Abgeschlossen', status: ['Erledigt'] },
  ];

  const [isInfoboxOpen, setIsInfoboxOpen] = useState(false);
  const [currentDamageReport, setCurrentDamageReport] = useState(0);
  const getFilteredReports = useCallback(
    (status: string[]) =>
      allReports?.filter(
        report => report.schad_status && status.includes(report.schad_status),
      ),
    [allReports],
  );

  const TabPanel = (panelProps: TabPanelProps) => {
    const { index, status } = panelProps;
    if (activeCategoryIndex !== index) return null;

    const openDamageReport = (schadenId: number | null | undefined) => {
      setCurrentDamageReport(schadenId ?? 0);
      setIsInfoboxOpen(true);
    };

    const renderReports = () => {
      const filteredReports = getFilteredReports(status);
      return filteredReports
        ?.sort((a, b) => moment(b.schad_datum).diff(moment(a.schad_datum)))
        .map(report => (
          <CardActionArea
            key={report.schad_id}
            onClick={() => openDamageReport(report.schad_id)}
            sx={{ width: '400px', margin: 1 }}
          >
            <ReportBox
              key={report.schad_id}
              m={0}
              sx={{ height: isMobile ? '180px' : '200px' }}
            >
              <Typography
                variant="inherit"
                color="secondary"
                sx={{
                  fontSize: 12,
                  fontWeight: 'light',
                  textTransform: 'uppercase',
                  letterSpacing: 1.1,
                }}
              >
                Schaden
              </Typography>
              <Typography variant="h5" color="secondary">
                {report.schad_art}
              </Typography>
              <Typography
                variant="inherit"
                color="secondary"
                sx={{ fontSize: 12, fontWeight: 'light', lineHeight: '1.5rem' }}
              >
                {moment(report.schad_datum).format('DD.MM.YYYY')}
              </Typography>
              <Typography variant="inherit" paddingTop={2} color="secondary">
                {report.schad_beschr || 'Keine Beschreibung'}
              </Typography>
            </ReportBox>
          </CardActionArea>
        ));
    };
    return (
      <ReportBoxContainer m={2}>
        {activeCategoryIndex === index && (
          <Box sx={{ p: isMobile ? 0 : 3 }}>
            <Box display="flex" flexDirection="row" flexWrap="wrap">
              {renderReports()}{' '}
            </Box>
            {isInfoboxOpen && currentDamageReport && (
              <ReportsInformationDialog
                open={isInfoboxOpen}
                onClose={() => setIsInfoboxOpen(false)}
                objId={Number(currentEstate?.objId)}
                customerId={Number(currentEstate?.customerId)}
                schadenId={currentDamageReport}
              />
            )}
          </Box>
        )}
      </ReportBoxContainer>
    );
  };

  return (
    <Container sx={{ padding: isMobile ? 0 : '20px' }}>
      <Box sx={{ borderBottom: 0, borderColor: 'divider', paddingLeft: 2 }}>
        <Tabs
          value={activeCategoryIndex}
          onChange={handleChange}
          variant="scrollable"
        >
          {categories.map(category => (
            <Tab key={category.id} label={category.label} />
          ))}
        </Tabs>
      </Box>
      {categories.map((category, index) => (
        <TabPanel key={category.id} index={index} status={category.status} />
      ))}
    </Container>
  );
});

export default ReportsTabs;
